import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "@beweegbaas/ui";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { LessonLayout } from "#/layouts/layout.lesson";
import { SettingsPopup } from "#/modules/les.leerling-gestuurd/components/settings-popup";
import {
  getStudentLessonLayout,
  lockStudentLesson,
} from "#/modules/les.leerling-gestuurd/lesson-student.functions";
import { useGlobalSoundOnMouseDown } from "#/modules/util.hooks/use-global-click-sound";
import { m } from "#/paraglide/messages";

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/lesson/$lesson/student",
)({
  // Chromeless student-led lesson view — opt out of the `$school` default layout
  // (portal's `handle.layout === false`).
  staticData: { layout: false },
  // Lock on entry: reaching the leerling-gestuurd (/student) view by a real client navigation must
  // lock the session. This runs whenever the session is not ALREADY locked — it is the reliable
  // fallback for the teacher "Leerling gestuurd" button, whose own pre-navigation `lockStudentLesson`
  // POST can be lost under load (dex 81cr6lnq, signature 2). Crucially it does NOT gate on
  // `activeLes.lesId === params.lesson`: a freshly started les is not necessarily the school's
  // activeLes (the seed's active les can win), so gating on that match skipped the lock exactly on
  // the path that flaked. When the button lock DID persist, the `$school` guard already redirected to
  // the active les's student view before this child beforeLoad runs, so this only fires when the lock
  // was actually missing.
  beforeLoad: async ({ context, params, preload }) => {
    // Never lock on hover-preload (`defaultPreload: "intent"` runs beforeLoad on hover);
    // locking is a mutation that must only happen on a real navigation into the view.
    if (preload) return;
    if (context.lockedLes === null) {
      await lockStudentLesson({ data: { lesson: params.lesson, school: params.school } });
    }
  },
  loader: ({ params }) => getStudentLessonLayout({ data: { lesson: params.lesson } }),
  // Children save resultaten while this layout stays matched ("stay" cause), which never
  // reloads by default — refetch on every real navigation so saved scores are visible.
  shouldReload: ({ cause }) => cause !== "preload",
  component: StudentLessonLayoutRoute,
  notFoundComponent: StudentLessonNotFound,
});

function StudentLessonNotFound() {
  return (
    <main className="flex min-h-svh items-center justify-center">
      <Empty>
        <EmptyHeader className="max-w-3xl">
          <EmptyTitle className="font-heading text-4xl font-black">
            {m["les.leerling.not_found_title"]()}
          </EmptyTitle>
          <EmptyDescription className="text-2xl/relaxed">
            {m["les.leerling.not_found_description"]()}
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
      <SettingsPopup className="fixed right-4 bottom-4" />
    </main>
  );
}

function StudentLessonLayoutRoute() {
  useGlobalSoundOnMouseDown();

  return (
    <LessonLayout>
      <Outlet />
    </LessonLayout>
  );
}
